import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ToggleLeft, ToggleRight, RefreshCw, RotateCcw } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin/features';

const FEATURE_ICONS = {
  virtual_keys: '🔑', booking_portal: '📅', guest_app_pwa: '📱',
  smart_home: '🏠', supplier_portal: '🤝', revenue_export: '📊',
  i18n_editor: '🌐', white_label: '✨', multi_property: '🏢',
  ai_booking_assistant: '🤖', rate_calendar: '📆', access_log_detailed: '📋',
  gdpr_tools: '🛡️', api_access: '⚡',
};

export function FeatureFlagsTab({ storedKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [localFlags, setLocalFlags] = useState({});

  const headers = { 'X-Superadmin-Key': storedKey };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, { headers });
      setData(res.data);
      if (res.data.tenants?.length > 0 && !selectedSlug) {
        const first = res.data.tenants[0];
        setSelectedSlug(first.slug);
        setLocalFlags({ ...first.flags });
      }
    } catch { toast.error('Errore caricamento feature flags.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selectTenant = (tenant) => {
    setSelectedSlug(tenant.slug);
    setLocalFlags({ ...tenant.flags });
  };

  const toggleFlag = (key) => {
    setLocalFlags(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    if (!selectedSlug) return;
    setSaving(selectedSlug);
    try {
      await axios.put(`${API}/${selectedSlug}`, localFlags, { headers });
      toast.success(`Feature flags di "${selectedSlug}" aggiornate.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore.'); }
    finally { setSaving(null); }
  };

  const reset = async () => {
    if (!selectedSlug) return;
    try {
      await axios.post(`${API}/${selectedSlug}/reset`, {}, { headers });
      toast.success('Ripristinato ai default del piano.');
      load();
    } catch { toast.error('Errore.'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
    </div>
  );

  const selectedTenant = data?.tenants?.find(t => t.slug === selectedSlug);

  return (
    <div data-testid="feature-flags-tab" className="flex gap-6">
      {/* Tenant selector */}
      <div className="w-56 shrink-0">
        <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>Tenant</p>
        <div className="space-y-1">
          {data?.tenants?.map(t => (
            <button key={t.slug} onClick={() => selectTenant(t)}
              className="w-full text-left px-3 py-2.5 rounded-sm transition-all text-xs"
              style={{
                background: selectedSlug === t.slug ? 'rgba(201,168,76,0.1)' : 'var(--lux-surface-1)',
                border: `1px solid ${selectedSlug === t.slug ? 'rgba(201,168,76,0.3)' : 'var(--lux-border-1)'}`,
                color: selectedSlug === t.slug ? 'var(--lux-gold)' : 'var(--lux-text-muted)',
              }}>
              <p className="font-medium truncate">{t.name}</p>
              <p style={{ color: 'var(--lux-text-muted-3)' }}>{t.plan}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Flags editor */}
      {selectedTenant && (
        <div className="flex-1">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-serif text-lg font-light" style={{ color: 'var(--lux-text)' }}>{selectedTenant.name}</h3>
              <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>Piano: {selectedTenant.plan} · Clicca per abilitare/disabilitare</p>
            </div>
            <div className="flex gap-2">
              <button onClick={reset} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm"
                style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                <RotateCcw size={11} /> Reset piano
              </button>
              <button onClick={save} disabled={!!saving} className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-sm font-medium"
                style={{ background: 'var(--lux-gold)', color: '#000' }}>
                {saving ? <RefreshCw size={11} className="animate-spin" /> : null} Salva modifiche
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(data?.available_features || {}).map(([key, meta]) => {
              const isOn = localFlags[key] ?? false;
              return (
                <div key={key} onClick={() => toggleFlag(key)}
                  className="flex items-center justify-between px-4 py-3.5 rounded-sm cursor-pointer transition-all"
                  style={{
                    background: isOn ? 'rgba(201,168,76,0.05)' : 'var(--lux-surface-1)',
                    border: `1px solid ${isOn ? 'rgba(201,168,76,0.25)' : 'var(--lux-border-1)'}`,
                  }}>
                  <div className="flex items-center gap-3">
                    <span className="text-base">{FEATURE_ICONS[key] || '⚙️'}</span>
                    <div>
                      <p className="text-sm" style={{ color: isOn ? 'var(--lux-text)' : 'var(--lux-text-muted)' }}>{meta.label}</p>
                      <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{key}</p>
                    </div>
                  </div>
                  {isOn
                    ? <ToggleRight size={22} style={{ color: 'var(--lux-gold)' }} />
                    : <ToggleLeft size={22} style={{ color: 'var(--lux-border-2)' }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
